import React from 'react';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/core/styles';
import AppBar from '@material-ui/core/AppBar';
import Tabs from '@material-ui/core/Tabs';
import Tab from '@material-ui/core/Tab';
import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import DenyReasonPanel from '../DenyReasonPanel';
import CardHolderDetails from '../CardHolderDetails';
import JobRoles from '../JobRoles';
import Blocks from '../Blocks';
import Competencies from '../Competencies';
import DialogHeader from 'components/DialogHeader';
import { hasCompetencies } from 'areas/competence/domain';
import { Mobile, Default } from 'components/Responsive';
import SwipeableViews from 'react-swipeable-views';
import classNames from 'classnames';
import DisplayMode from 'areas/swipes/domain/displayMode';
import styles from './styles';
import m from 'messages';

class CardHolderView extends React.Component {

    constructor(props) {
        super(props);
        this.state = {
            selectedTab: 0
        };

        this.blockRef = React.createRef();
        this.scrollRef = React.createRef();
    }

    handleClose = () => {
        this.props.close();
    };

    handleTabChange = (_, value) => {
        this.setState({ selectedTab: value });
    };

    handleIndexChange = index => this.setState({ selectedTab: index });

    gotoBlocksTab = () => {
        const offset = this.blockRef.current ? this.blockRef.current.offsetTop : 0;
        if (offset > 0 && this.scrollRef.current && this.scrollRef.current.parentNode) {
            this.scrollRef.current.parentNode.scrollTop = offset - 80;
        }
        this.setState({ selectedTab: 1 });
    };

    tabs(card, config, denyReason, editable, cardHolderProps, location) {
        const tabArray = [];
        const tabPanels = [];

        const isMinimal =
            card.isMinimalCardDisplay
            || (this.props.viewcontext === DisplayMode.swipeOut || this.props.viewcontext === DisplayMode.showCardSwipeOut);

        const isMinimalWithBlocksShown =
            card.isMinimalCardDisplayShowingBlocks
            || (this.props.viewcontext === DisplayMode.swipeOut || this.props.viewcontext === DisplayMode.showCardSwipeOut);

        tabArray.push(<Tab label={m.cards.cardDetailsTab} key="card" />);

        tabPanels.push(
            <div>
                <CardHolderDetails
                    denyReason={denyReason}
                    showBlockLink={true}
                    employerAndJobRoleEditable={!!editable}
                    card={card}
                    config={config}
                    location={location}
                    onShowBlocks={this.gotoBlocksTab}
                    viewcontext={this.props.viewcontext}
                    {...cardHolderProps} />
            </div>);

        if ((isMinimalWithBlocksShown || !isMinimal) && card.suspensions && card.suspensions.length > 0) {
            tabArray.push(<Tab label={m.cards.blocksTab} key="blocks" />);
            tabPanels.push(<div ref={this.blockRef}><Blocks card={card} /></div>);
        }

        if ((!isMinimalWithBlocksShown && !isMinimal) && (card.jobRolesArray && card.jobRolesArray.length) > 0) {
            tabArray.push(<Tab label={m.cards.jobRolesTab} key="jobs" />);
            tabPanels.push(<JobRoles card={card} />);
        }

        if ((!isMinimalWithBlocksShown && !isMinimal) && hasCompetencies(card.competencies, 'national')) {
            tabArray.push(<Tab label={m.cards.nationalCompetenciesTab} key="national" />);
            tabPanels.push(<Competencies card={card} filter='national' title={m.cards.nationalCompetenciesTitle} />);
        }

        if ((!isMinimalWithBlocksShown && !isMinimal) && hasCompetencies(card.competencies, 'employerID')) {
            tabArray.push(<Tab label={m.cards.employerCompetenciesTab} key="employer" />);
            tabPanels.push(<Competencies card={card} filter='employerID' displayProp='employerName' title={m.cards.employerCompetenciesTitle} />);
        }

        if ((!isMinimalWithBlocksShown && !isMinimal) && hasCompetencies(card.competencies, 'divisionID')) {
            tabArray.push(<Tab label={`${config.divisionLevelName} based`} key="division" />);
            tabPanels.push(<Competencies card={card} filter='divisionID' displayProp='divisionName' title={m.cards.namedCompetenciesTitle(config.divisionLevelName)} />);
        }

        if ((!isMinimalWithBlocksShown && !isMinimal) && hasCompetencies(card.competencies, 'projectID')) {
            tabArray.push(<Tab label={`${config.projectLevelName} based`} key="project" />);
            tabPanels.push(<Competencies card={card} filter='projectID' displayProp='projectName' title={m.cards.namedCompetenciesTitle(config.projectLevelName)} />);
        }

        if ((!isMinimalWithBlocksShown && !isMinimal) && hasCompetencies(card.competencies, 'siteName')) {
            tabArray.push(<Tab label={m.cards.siteOrZoneCompetenciesTab(config.siteLevelName)} key="site" />);
            tabPanels.push(<Competencies card={card} filter='siteName' invertedFilter='zoneName' displayProp='siteName' title={m.cards.siteOrZoneCompetenciesTitle(config.siteLevelName)} />);
        }

        if ((!isMinimalWithBlocksShown && !isMinimal) && hasCompetencies(card.competencies, 'zoneName')) {
            tabArray.push(<Tab label={m.cards.siteOrZoneCompetenciesTab(config.zoneLevelName)} key="zone" />);
            tabPanels.push(<Competencies card={card} filter='zoneName' displayProp='zoneName' title={m.cards.siteOrZoneCompetenciesTitle(config.zoneLevelName)} />);
        }

        return { tabs: tabArray, panels: tabPanels };
    }

    render() {
        const {
            card,
            classes,
            config,
            employerAndJobRoleEditable,
            cardHolderProps,
            denyReason,
            disableBackButton,
            location } = this.props;
        const { selectedTab } = this.state;

        if (!Boolean(card)) {
            return (<React.Fragment></React.Fragment>);
        }

        const { tabs, panels } =
            this.tabs(card, config, denyReason, employerAndJobRoleEditable, cardHolderProps, location);

        const actions = React.Children.map(this.props.children, (child, index) =>
            React.cloneElement(child, {
                key: index,
                className: classNames(classes.actionButtons, child.props.className)
            }));

        return (
            <div>
                {card &&
                    <Dialog
                        fullScreen
                        open={true}>
                        <DialogHeader
                            title={this.props.title}
                            handleClose={this.handleClose}
                            disableClose={!!disableBackButton}
                            showBackIcon={true}
                            icon={this.props.icon} />
                        <DialogContent className={classes.contentRoot}>
                            <Mobile>
                                <AppBar position="static" color="default" elevation={0}>
                                    <Tabs
                                        value={selectedTab}
                                        onChange={this.handleTabChange}
                                        indicatorColor="primary"
                                        textColor="primary"
                                        variant="scrollable"
                                        scrollButtons="on">
                                        {tabs}
                                    </Tabs>
                                </AppBar>
                                <SwipeableViews axis="x" index={selectedTab} onChangeIndex={this.handleIndexChange}>
                                    {panels.map((p, i) =>
                                        <div className={classes.contentBody} key={i}>
                                            <DenyReasonPanel denyReason={denyReason} />
                                            {p}
                                        </div>
                                    )}
                                </SwipeableViews>
                            </Mobile>
                            <Default>
                                <div className={classes.contentBodyWide} ref={this.scrollRef}>
                                    <DenyReasonPanel denyReason={denyReason} />
                                    {panels.map((p, i) =>
                                        <section className={classes.section} key={i}>
                                            {p}
                                        </section>
                                    )}
                                </div>
                            </Default>
                        </DialogContent>
                        <DialogActions className={classes.actionRoot}>
                            {actions}
                        </DialogActions>
                    </Dialog>
                }
            </div>);
    }

}

CardHolderView.propTypes = {
    card: PropTypes.object,
    location: PropTypes.object,
    config: PropTypes.object,
    employerAndJobRoleEditable: PropTypes.bool,
    denyReason: PropTypes.object,
    disableBackButton: PropTypes.bool,
    icon: PropTypes.func,
    viewcontext: PropTypes.oneOf([
        'none',
        'historic',
        'swipeIn',
        'swipeOut',
        'spotCheck',
        'spotCheckJobRoleSelection',
        'competence',
        'awardCompetence']).isRequired
};

export default withStyles(styles)(CardHolderView);
